import React, { useState, useRef } from "react";
import { IconButton } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import { cancelarVoz } from "../../util/voice";
import { StopCircle } from "@mui/icons-material";

interface AudioParaTextoProps {
  onTranscricao?: (texto: string) => void;
  loading?: boolean;
}

const AudioParaTexto: React.FC<AudioParaTextoProps> = ({
  onTranscricao,
  loading,
}) => {
  const [listening, setListening] = useState(false);
  const [notSuported, setNotSupported] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const pressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isHoldingRef = useRef(false);

  const startRecognition = () => {
    const SpeechRecognition =
      (window as any)?.SpeechRecognition ||
      (window as any)?.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setNotSupported(true);
      alert("Este navegador não suporta a Web Speech API.");
      return;
    }
    cancelarVoz();

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.lang = "pt-BR";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const resultado = event.results[0][0].transcript;
      onTranscricao?.(resultado);
    };

    recognition.onerror = (event: any) => {
      console.error("Erro:", event.error);
      setNotSupported(true);
      if (isHoldingRef.current) {
        setListening(false);
        isHoldingRef.current = false;
      }
    };

    recognition.onend = () => {
      setListening(false);
      isHoldingRef.current = false;
    };

    recognition.start();
  };

  const stopRecognition = () => {
    recognitionRef.current?.stop();
    if (!isHoldingRef.current) {
      setListening(false);
    }
  };

  const handleMouseDown = () => {
    window?.dispatchEvent(new Event("stopAllAudios"));
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
    }

    isHoldingRef.current = true;

    pressTimerRef.current = setTimeout(() => {
      if (!listening) {
        startRecognition();
      }
    }, 300);
  };

  const handleMouseUp = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }

    isHoldingRef.current = false;

    if (
      listening &&
      recognitionRef.current &&
      recognitionRef.current.continuous
    ) {
      stopRecognition();
    } else if (listening) {
      stopRecognition();
    } else {
      startRecognition();
    }
  };

  const handleClick = () => {
    if (pressTimerRef.current === null && !isHoldingRef.current) {
      if (listening) {
        stopRecognition();
      } else {
        startRecognition();
      }
    }
  };

  return (
    <IconButton
      color={listening ? "error" : "primary"}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
      disabled={loading || notSuported}
    >
      {listening ? <StopCircle /> : <MicIcon />}
    </IconButton>
  );
};

export default AudioParaTexto;
